import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  LayoutGrid,
  AlertCircle,
  Activity,
  X,
  Zap,
  Crown,
  CheckCircle2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { usePortfolio } from '../context/PortfolioContext';
import { useTranslation } from 'react-i18next';
import { CexAccount } from '../constants';
import { useCexAccount, ALL_ACCOUNTS_ID } from '../context/CexAccountContext';
import AddCexAccountModal from '../components/AddCexAccountModal';
import SyncLoadingModal from '../components/SyncLoadingModal';
import SuccessToast from '../components/SuccessToast';
import { ProductTour, TourStep } from '../components/ProductTour';
import { API_BASE_URL } from '../config/api';
import { useSubscription } from '../context/SubscriptionContext';

interface DashboardPageProps {
  formData: any;
}

import { useSEO } from '../hooks/useSEO';

const getDynamicFontSizeStyle = (val: string, baseSizeMobile = 20, baseSizeDesktop = 30) => {
  if (!val || typeof val !== 'string') return {};
  const len = val.length;
  if (len <= 7) return {};
  const reduction = (len - 7) * 0.85;
  const sizeMobile = Math.max(12, baseSizeMobile - reduction);
  const sizeDesktop = Math.max(14, baseSizeDesktop - reduction);
  return {
    fontSize: `clamp(${sizeMobile}px, 2vw, ${sizeDesktop}px)`
  };
};

const DashboardPage: React.FC<DashboardPageProps> = ({ formData }) => {
  useSEO({
    title: 'Dashboard',
    description: 'Dashboard utama CatatCrypto. Lihat total saldo portofolio, metrik win rate, keuntungan harian, grafik pertumbuhan, dan status akun CEX Anda.',
    keywords: 'dashboard, crypto tracker, trade analytics, win rate, performance tracking'
  });

  const { portfolioData, setPortfolioData, lastSyncedAccountId, setLastSyncedAccountId } = usePortfolio();
  const cexContext = useCexAccount();
  const { selectedAccount, setSelectedAccount: setSelectedAccountContext, selectedWalletAccount } = cexContext;
  const { t } = useTranslation();
  const { effectivePlan, isLoading: isSubLoading } = useSubscription();

  const tourSteps: TourStep[] = [
    {
      target: 'body',
      title: "Welcome to Catat-Crypto!",
      content: "We have put together a quick guide to introduce the navigation area and trading features in this dashboard. Click Next to start!",
      position: 'center'
    },
    {
      target: '[data-tour="sidebar-nav"]',
      title: "Main Navigation Menu",
      content: "Quick access to your Dashboard, in-depth trading Reports, transaction records (Trades), and daily Journal (Notebook).",
      position: 'right'
    },
    {
      target: '[data-tour="topbar-cex"]',
      title: "Select Your Exchange Account",
      content: "Switch between connected exchange portfolios (like Binance, Indodax, OKX, Tokocrypto) instantly to filter all your daily charts.",
      position: 'bottom'
    },
    {
      target: '[data-tour="dashboard-balance"]',
      title: "Account Balance & Asset Curve",
      content: "Displays your net cumulative balance from connected exchange accounts, complete with a trend graph of the equity curve growth.",
      position: 'left'
    },
    {
      target: '[data-tour="dashboard-winrate"]',
      title: "Catat-Crypto Score Analytics",
      content: "Multidimensional strength analysis of your trading performance (Win Rate, Daily Consistency, Risk Management). Target an A+ score!",
      position: 'right'
    },
    {
      target: '[data-tour="sidebar-ai"]',
      title: "AI Trade Assistant",
      content: "Your smart AI assistant. Ready to analyze journal psychology, summarize weekly trading performance, and review strategy setups.",
      position: 'right'
    }
  ];

  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [dataErrors, setDataErrors] = useState<Record<string, string>>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const isFirstMount = React.useRef(true);
  const hasFirstSyncDone = React.useRef(false);
  const [accountRefreshKey, setAccountRefreshKey] = useState(0);

  const [showPricingPopup, setShowPricingPopup] = useState(false);
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [popupError, setPopupError] = useState('');
  const [waitingPaymentPlan, setWaitingPaymentPlan] = useState<string | null>(null);
  const [triggerCount, setTriggerCount] = useState(0);

  const { subscription, refresh } = useSubscription();

  // Poll subscription changes if waiting for payment
  useEffect(() => {
    if (!waitingPaymentPlan) return;
    const interval = setInterval(async () => {
      await refresh();
    }, 3000);
    return () => clearInterval(interval);
  }, [waitingPaymentPlan, refresh]);

  // Auto-submit partnership draft if exists
  useEffect(() => {
    const pendingApp = localStorage.getItem('pending_partnership_application');
    if (!pendingApp) return;

    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const submitDraft = async () => {
      try {
        const payload = JSON.parse(pendingApp);
        const res = await fetch(`${API_BASE_URL}/api/partnership/apply`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            localStorage.removeItem('pending_partnership_application');
            window.location.href = '/referral';
          }
        }
      } catch (err) {
        console.error('Failed to auto-submit partnership application draft:', err);
      }
    };

    submitDraft();
  }, []);

  // Monitor plan change to dismiss the pricing popup
  useEffect(() => {
    if (waitingPaymentPlan && subscription?.plan === waitingPaymentPlan) {
      setWaitingPaymentPlan(null);
      setShowPricingPopup(false);
    }
  }, [subscription, waitingPaymentPlan]);



  const handleSubscribe = async (planId: string) => {
    setLoadingPlan(planId);
    setPopupError('');
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/subscription/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan: planId, cycle }),
      });
      const data = await res.json();
      if (res.status === 409 && data.payment_url) {
        window.open(data.payment_url, '_blank');
        setWaitingPaymentPlan(planId);
        return;
      }
      if (!res.ok) {
        setPopupError(data.message || 'Gagal membuat invoice');
        return;
      }
      if (data.payment_url) {
        window.open(data.payment_url, '_blank');
        await refresh();
        setWaitingPaymentPlan(planId);
      }
    } catch {
      setPopupError('Koneksi gagal. Pastikan backend server menyala.');
    } finally {
      setLoadingPlan(null);
    }
  };

  // Demo Mode States
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [hasSetDefaultDemo, setHasSetDefaultDemo] = useState(false);

  // Interactive Hover States
  const [hoveredRadar, setHoveredRadar] = useState<{ x: number; y: number; label: string; value: string } | null>(null);
  const [hoveredEquityPoint, setHoveredEquityPoint] = useState<{
    x: number;
    y: number;
    balance: string;
    date: string;
    pnl?: string;
    positive?: boolean;
  } | null>(null);

  // Wrapper for setSelectedAccount that also saves to localStorage
  const handleAccountChange = (account: CexAccount) => {
    setSelectedAccountContext(account);
  };

  const monthNames = [
    t('dashboard.january') || "January",
    t('dashboard.february') || "February",
    t('dashboard.march') || "March",
    t('dashboard.april') || "April",
    t('dashboard.may') || "May",
    t('dashboard.june') || "June",
    t('dashboard.july') || "July",
    t('dashboard.august') || "August",
    t('dashboard.september') || "September",
    t('dashboard.october') || "October",
    t('dashboard.november') || "November",
    t('dashboard.december') || "December"
  ];

  // currentDate representation optimized for September 2024 calendar layout in Demo Mode
  const currentDateForDisplay = isDemoMode ? new Date(2024, 8, 1) : currentDate;

  // 100% Figma-Matched mock portfolio data
  const mockPortfolioData = React.useMemo(() => {
    const mockPerf = [
      { date: "01", realizedValue: 1240, positive: true, neutral: false, realized: "+$1,240", barLabel: "+$1,240", tradesCount: 4 },
      { date: "02", realizedValue: -420, positive: false, neutral: false, realized: "-$420", barLabel: "-$420", tradesCount: 2 },
      { date: "03", realizedValue: 0, positive: false, neutral: true, realized: "NO TRADES", barLabel: "NO TRADES", tradesCount: 0 },
      { date: "04", realizedValue: 890, positive: true, neutral: false, realized: "+$890", barLabel: "+$890", tradesCount: 7 },
      { date: "05", realizedValue: 2100, positive: true, neutral: false, realized: "+$2,100", barLabel: "+$2,100", tradesCount: 1 },
      { date: "07", realizedValue: -85, positive: false, neutral: false, realized: "-$85", barLabel: "-$85", tradesCount: 1 },
      { date: "09", realizedValue: 4200, positive: true, neutral: false, realized: "+$4,200", barLabel: "+$4,200", tradesCount: 3 },
      { date: "14", realizedValue: -85, positive: false, neutral: false, realized: "-$85", barLabel: "-$85", tradesCount: 0 }
    ];

    return {
      totalBalance: "$148,900.00",
      netPnl: "+$12,482",
      netPnlPercent: "14%",
      netPnlPositive: true,
      dayWinRate: "72.1%",
      dayWinRateSub: "31 / 43 DAYS",
      tradeWinRate: "68.4%",
      tradeWinRateSub: "84 / 123 TRADES",
      avgWinLoss: "+$420 / -$185",
      rrRatio: "RR: 2.27",
      performance: mockPerf,
      activeDeployments: [
        {
          id: 1,
          asset: "BTC / USDT",
          type: "LONG (20X)",
          entry: "$64,120.45",
          size: "0.45 BTC",
          pnl: "+$842.10",
          percent: "+1.2%",
          duration: "04:12:35",
          positive: true,
          exchange: ""
        },
        {
          id: 2,
          asset: "ETH / USDT",
          type: "SHORT (10X)",
          entry: "$2,410.15",
          size: "12.50 ETH",
          pnl: "-$154.20",
          percent: "-0.6%",
          duration: "01:45:12",
          positive: false,
          exchange: ""
        }
      ]
    };
  }, []);

  // Determine active dataset (live vs demo)
  const displayPortfolioData = isDemoMode ? mockPortfolioData : portfolioData;

  const handleResync = async (showToast: boolean = true, fastMode: boolean = false) => {
    setIsSyncing(true);
    setSyncError('');
    setDataErrors({});

    try {
      const token = localStorage.getItem('auth_token');
      
      let url = '';
      if (selectedWalletAccount) {
        url = `${API_BASE_URL}/api/wallet-accounts/${selectedWalletAccount.id}/sync`;
      } else {
        const isAllAccounts = !selectedAccount || selectedAccount.id === ALL_ACCOUNTS_ID;
        url = isAllAccounts
          ? `${API_BASE_URL}/api/cex-accounts/sync-all`
          : `${API_BASE_URL}/api/cex-accounts/${selectedAccount.id}/sync`;
      }

      if (fastMode) {
        url += (url.includes('?') ? '&' : '?') + 'fast_mode=1';
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to sync');
      }

      setPortfolioData(data.data);
      validatePortfolioData(data.data);

      if (selectedWalletAccount) {
        setLastSyncedAccountId(`wallet-${selectedWalletAccount.id}`);
      } else {
        const currentCexId = selectedAccount ? selectedAccount.id : ALL_ACCOUNTS_ID;
        if (currentCexId === ALL_ACCOUNTS_ID) {
          setLastSyncedAccountId('cex-all');
        } else {
          setLastSyncedAccountId(`cex-${currentCexId}`);
        }
      }

      if (data.sync_errors && Object.keys(data.sync_errors).length > 0) {
        const failedExchanges = Object.keys(data.sync_errors)
          .map(cex => `${cex.toUpperCase()}: ${data.sync_errors[cex]}`)
          .join('\n');
        setSyncError(`Warning: Beberapa exchange gagal sinkronisasi:\n${failedExchanges}`);
      } else {
        setSyncError('');
      }

      if (showToast && (!data.sync_errors || Object.keys(data.sync_errors).length === 0)) {
        setShowSuccessToast(true);
      }
    } catch (e: any) {
      if (selectedAccount || selectedWalletAccount) {
        setSyncError(e.message || 'Failed to sync. Please check your credentials/address.');
      } else {
        console.warn('Failed to sync manual trades:', e);
      }
    } finally {
      setIsSyncing(false);
      hasFirstSyncDone.current = true;
    }
  };

  const handleAccountAdded = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/cex-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success && data.data.length > 0) {
        const newestAccount = data.data[0];
        handleAccountChange(newestAccount);
        setAccountRefreshKey(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to fetch accounts after creation:', error);
    }

    setIsAddModalOpen(false);
  };

  // Validate each data section and set specific errors
  const validatePortfolioData = (data: any) => {
    const errors: Record<string, string> = {};

    if (!data) {
      errors.global = t('dashboard.noDataFromApi') || 'Tidak ada data dari API';
      setDataErrors(errors);
      return;
    }

    if (!data.netPnl || data.netPnl === '$0.00') {
      errors.netPnl = t('dashboard.noTradingHistory');
    }

    if (!data.dayWinRate || data.dayWinRate === '0%') {
      errors.dayWinRate = t('dashboard.noTradingHistory');
    }

    if (!data.tradeWinRate || data.tradeWinRate === '0%') {
      errors.tradeWinRate = t('dashboard.noTradingHistory');
    }

    if (!data.avgWinLoss || data.avgWinLoss === '$0 / $0') {
      errors.avgWinLoss = t('dashboard.noTradingHistory');
    }

    if (!data.tradeWinRate || parseFloat(data.tradeWinRate) === 0) {
      errors.score = t('dashboard.noDataToCalculate');
    }

    if (!data.totalBalance || data.totalBalance === '$0.00') {
      errors.equity = t('dashboard.balanceNotAvailable');
    }

    if (!data.performance || data.performance.length === 0) {
      errors.performance = t('dashboard.noTradingHistory');
    }

    if (!data.activeDeployments || data.activeDeployments.length === 0) {
      errors.deployments = t('dashboard.noOpenPositions');
    }

    setDataErrors(errors);
  };

  // Auto-sync or auto-toggle demo mode based on synced portfolio data
  useEffect(() => {
    if (portfolioData && !hasSetDefaultDemo) {
      setIsDemoMode(false);
      setHasSetDefaultDemo(true);
    } else if (!portfolioData && !hasSetDefaultDemo) {
      setIsDemoMode(true);
    }
  }, [portfolioData, hasSetDefaultDemo]);

  // Auto-sync when account changes OR on first mount
  useEffect(() => {
    if (effectivePlan === 'expired') {
      return;
    }

    if (isSyncing) return;

    let selectedAccountId = 'cex-all';
    if (selectedWalletAccount) {
      selectedAccountId = `wallet-${selectedWalletAccount.id}`;
    } else if (selectedAccount) {
      selectedAccountId = selectedAccount.id === ALL_ACCOUNTS_ID ? 'cex-all' : `cex-${selectedAccount.id}`;
    }

    if (portfolioData && lastSyncedAccountId === selectedAccountId) {
      validatePortfolioData(portfolioData);
      isFirstMount.current = false;
      return;
    }

    if (isFirstMount.current) {
      isFirstMount.current = false;

      if (portfolioData && lastSyncedAccountId === selectedAccountId) return;

      if (selectedAccountId !== 'cex-all') {
        setPortfolioData(null);
      }
      handleResync(false, true);
      return;
    }

    if (selectedAccountId !== lastSyncedAccountId) {
      setPortfolioData(null);
      handleResync(false, true);
    }
  }, [selectedAccount?.id, selectedWalletAccount?.id, lastSyncedAccountId]);

  // Run validation on mount / display data change — only after first sync is done
  React.useEffect(() => {
    if (displayPortfolioData && !isSyncing && hasFirstSyncDone.current) {
      validatePortfolioData(displayPortfolioData);
    }
    // Clear errors while syncing so badges don't flash
    if (isSyncing) {
      setDataErrors({});
    }
  }, [displayPortfolioData, t, isSyncing]);

  // Listen for cex_account_added event dispatched by TopBar — auto-select newest account and sync
  React.useEffect(() => {
    const handleCexAccountAdded = (e: Event) => {
      const customEvent = e as CustomEvent;
      const accountsList = customEvent.detail?.accounts || cexContext.accounts;
      if (accountsList && accountsList.length > 0) {
        const newest = accountsList[accountsList.length - 1];
        setSelectedAccountContext(newest);
      }
    };
    window.addEventListener('cex_account_added', handleCexAccountAdded);
    return () => window.removeEventListener('cex_account_added', handleCexAccountAdded);
  }, [cexContext.accounts, setSelectedAccountContext]);


  const handlePrevMonth = () => {
    if (isDemoMode) return; // Lock calendar in Demo Mode
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    if (isDemoMode) return; // Lock calendar in Demo Mode
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const daysInMonth = new Date(currentDateForDisplay.getFullYear(), currentDateForDisplay.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDateForDisplay.getFullYear(), currentDateForDisplay.getMonth(), 1).getDay();
  // 100% Figma Match: Wed 01 requires a 2 days offset. Lock startOffset to 2 in Demo Mode!
  const startOffset = isDemoMode ? 2 : (firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1);

  const balance = displayPortfolioData?.totalBalance || "$0.00";
  const pnl = displayPortfolioData?.netPnl || "$0.00";
  const pnlPercent = displayPortfolioData?.netPnlPercent || "0%";
  const pnlPositive = displayPortfolioData?.netPnlPositive ?? true;

  const dayWinRate = displayPortfolioData?.dayWinRate || "0%";
  const dayWinRateSub = displayPortfolioData?.dayWinRateSub || "0/0 Days";
  const tradeWinRate = displayPortfolioData?.tradeWinRate || "0%";
  const tradeWinRateSub = displayPortfolioData?.tradeWinRateSub || "0/0 Trades";
  const avgWinLoss = displayPortfolioData?.avgWinLoss || "$0 / $0";
  const rawRr = displayPortfolioData?.rrRatio || "RR: 0";
  const rrRatio = rawRr.includes("99.99") ? "RR: MAX" : rawRr;

  // Mouse move handler for equity curve horizontal cursor line and tooltip
  const handleEquityMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!displayPortfolioData?.performance || displayPortfolioData.performance.length === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const pctX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

    const points = isDemoMode 
      ? [10000, 20000, 45000, 35000, 68000, 80000, 122450, 95000, 130000, 148900, 148900]
      : (() => {
          let currentBal = parseFloat(balance.replace(/[^0-9.-]+/g, ''));
          if (isNaN(currentBal) || currentBal === 0) currentBal = 10000;
          const pts: number[] = [currentBal];
          for (let i = 0; i < displayPortfolioData.performance.length - 1; i++) {
            const item = displayPortfolioData.performance[i];
            const realizedVal = item && typeof item.realizedValue === 'number' && Number.isFinite(item.realizedValue)
              ? item.realizedValue
              : 0;
            currentBal -= realizedVal;
            pts.unshift(currentBal);
          }
          return pts;
        })();

    const maxB = isDemoMode ? 150000 : Math.max(...points) * 1.05;
    const minB = isDemoMode ? 0 : Math.min(...points) * 0.95;
    const range = maxB - minB === 0 ? 1 : maxB - minB;

    // In live mode, require at least 2 data points for a meaningful chart
    if (!isDemoMode && points.length < 2) return;

    const pointsCount = points.length;
    const idx = Math.round(pctX * (pointsCount - 1));
    const clampedIdx = Math.max(0, Math.min(pointsCount - 1, idx));

    const x = (clampedIdx / (pointsCount - 1)) * 100;
    const y = 100 - ((points[clampedIdx] - minB) / range) * 100;

    let dateStr = "Start of Month";
    let pnlStr = "";
    let positive = true;

    if (isDemoMode) {
      const dates = ["START", "WED 01", "THU 02", "FRI 03", "SAT 04", "SUN 05", "MON 06", "TUE 07", "SUN 09", "MON 10", "TUE 11"];
      dateStr = dates[clampedIdx] || "Sept 2024";
      const pnls = ["", "", "-$420.00", "NO TRADES", "+$890.00", "+$2,100.00", "", "-$85.00", "+$4,200.00", "", ""];
      pnlStr = pnls[clampedIdx] || "";
      positive = !pnlStr.includes("-");
    } else {
      if (clampedIdx > 0 && displayPortfolioData.performance[clampedIdx - 1]) {
        const perfItem = displayPortfolioData.performance[clampedIdx - 1];
        dateStr = `${monthNames[currentDateForDisplay.getMonth()].substring(0, 3)} ${perfItem.date}`;
        pnlStr = perfItem.realized;
        positive = perfItem.positive;
      }
    }

    const fmtBal = points[clampedIdx].toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });

    setHoveredEquityPoint({
      x,
      y,
      balance: fmtBal,
      date: dateStr,
      pnl: pnlStr || undefined,
      positive
    });
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar
        plan={formData.plan}
        onNewTradeClick={() => setIsAddModalOpen(true)}
      />

      <main className="flex-1 bg-[#050505] min-h-screen pt-16 md:pt-0 overflow-x-hidden">
        <TopBar
          title="Dashboard"
          name={formData.name}
          selectedAccount={selectedAccount}
          onAccountChange={handleAccountChange}
          refreshKey={accountRefreshKey}
          showAccountSwitcher={true}
          showDemoToggle={true}
          isDemoMode={isDemoMode}
          onDemoToggle={setIsDemoMode}
          showResync={true}
          isSyncing={isSyncing}
          onResync={() => handleResync(true)}
        />

        {/* Demo Mode active preview banner */}
        {isDemoMode && (
          <div className="mx-auto w-full max-w-[1132px] pt-4 md:pt-8 px-3 md:px-0">
            <div className="relative overflow-hidden bg-gradient-to-r from-[#0B0C10] to-[#12131A] border border-[#EF4444]/20 rounded-3xl p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 shadow-[0_0_50px_rgba(229,9,20,0.04)]">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/5 rounded-full blur-[80px] pointer-events-none" />
              <div className="absolute -left-12 -bottom-12 w-48 h-48 bg-neutral-950 rounded-full blur-[60px] pointer-events-none" />
              
              <div className="flex items-center gap-4 relative z-10 font-sans">
                <div className="w-12 h-12 rounded-2xl bg-[#EF4444]/10 border border-[#EF4444]/20 flex items-center justify-center text-[#EF4444] shrink-0 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                  <Activity size={22} className="animate-pulse" />
                </div>
                <div>
                  <h4 className="text-sm font-bold tracking-wide text-white">Preview Mode Active</h4>
                  <p className="text-xs text-neutral-400 mt-1 max-w-xl">
                    This is a preview of what the dashboard looks like once trade data is added. Try adding a manual trade or syncing your CEX account to see your real analytics!
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 shrink-0 relative z-10">
                <button 
                  onClick={() => {
                    window.location.href = '/trades?add=true';
                  }}
                  className="px-5 py-2.5 rounded-xl bg-[#E50914] hover:bg-[#E50914]/90 text-white text-xs font-bold transition-all shadow-lg shadow-red-950/20 hover:scale-105"
                >
                  Add Manual Trade
                </button>
                <button 
                  onClick={() => handleResync(true, false)}
                  className="px-5 py-2.5 rounded-xl bg-[#0E0F14] border border-[#181922] hover:border-neutral-700 text-neutral-300 hover:text-white text-xs font-bold transition-all hover:scale-105"
                >
                  Sync CEX Account
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="py-4 md:py-8 flex flex-col items-center w-full px-3 md:px-0 overflow-x-hidden">
          <div className="w-full max-w-[1132px] space-y-8">
          {/* Sync Error Alert */}
          {syncError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/20 border border-red-900/50 rounded-2xl p-4 flex items-center gap-3"
            >
              <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
              <div className="flex-1">
                 <p className="text-sm font-bold text-red-500 whitespace-pre-line">{syncError}</p>
                <p className="text-xs text-neutral-400 mt-1">{t('dashboard.syncErrorAction')}</p>
              </div>
              <button
                onClick={() => setSyncError('')}
                className="text-neutral-500 hover:text-white transition-colors"
              >
                ×
              </button>
            </motion.div>
          )}

          {/* Empty State Warning — only shown after first sync attempt is done */}
          {!displayPortfolioData && !isSyncing && hasFirstSyncDone.current && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-yellow-900/20 border border-yellow-900/50 rounded-2xl p-4 flex items-center gap-3"
            >
              <AlertCircle size={20} className="text-yellow-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-yellow-500">{t('dashboard.noData')}</p>
                <p className="text-xs text-neutral-400 mt-1">{t('dashboard.noDataAction')} {selectedAccount?.cex_display_name || t('dashboard.yourExchange')}.</p>
              </div>
            </motion.div>
          )}

          {/* Header */}
          <header className="space-y-1 select-none">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-black tracking-tight">
                  {t('dashboard.title')}
                </h1>
                <p className="text-neutral-500 text-sm">
                  {t('dashboard.subtitle')}
                </p>
              </div>
              {isDemoMode && (
                <div className="flex items-center gap-2 bg-red-950/20 border border-red-900/30 rounded-full px-4 py-2 select-none">
                  <div className="w-2 h-2 bg-[#E50914] rounded-full animate-pulse" />
                  <span className="text-xs font-bold text-[#E50914]">
                    DEMO SIMULATOR
                  </span>
                </div>
              )}
            </div>
          </header>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            <StatCard
              label={t('dashboard.netPnl')}
              value={pnl}
              subValue={pnlPercent}
              positive={pnlPositive}
              error={dataErrors.netPnl}
            />
            <StatCard
              label={t('dashboard.dayWinRate')}
              value={dayWinRate}
              subValue={dayWinRateSub}
              error={dataErrors.dayWinRate}
            />
            <StatCard
              label={t('dashboard.tradeWinRate')}
              value={tradeWinRate}
              subValue={tradeWinRateSub}
              error={dataErrors.tradeWinRate}
            />
            <StatCard
              label={t('dashboard.avgWinLoss')}
              value={avgWinLoss}
              subValue={rrRatio}
              custom
              error={dataErrors.avgWinLoss}
            />
          </div>

          {/* Charts Row - 100% Figma layout & dimensions: h-[502px] flex flex-row items-start gap-6 stretch */}
          <div className="flex flex-col lg:flex-row gap-6 font-sans">
            {/* Score Radar Chart - Left Card (h-[502px]) */}
            <div className="w-full lg:w-[356px] lg:flex-shrink-0">
              <ErrorBadge message={dataErrors.score} />
              <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 relative overflow-hidden select-none flex flex-col justify-between min-h-[420px] lg:h-[502px] w-full gap-6" data-tour="dashboard-winrate">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/[0.01] rounded-full blur-[80px] pointer-events-none" />
                <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest mb-2">{t('dashboard.catatCryptoScore')}</h3>
                
                {/* Premium blur overlay — non-premium users see blurred score on live page */}
                {!isDemoMode && effectivePlan !== 'premium' && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.55)' }}>
                    <div className="flex flex-col items-center gap-3 px-6 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-[#E50914]/10 border border-[#E50914]/20 flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E50914" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </div>
                      <p className="text-xs font-black uppercase tracking-wider text-white">Premium Only</p>
                      <p className="text-[10px] text-neutral-400 leading-relaxed">Upgrade to Premium to unlock your full Catat-Crypto Score analytics.</p>
                      <a href="/settings" className="mt-1 px-4 py-2 rounded-xl bg-[#E50914] hover:bg-[#E50914]/90 text-white text-[10px] font-bold uppercase tracking-wider transition-all hover:scale-105">
                        Upgrade Now
                      </a>
                    </div>
                  </div>
                )}
                
                <div className="relative flex-1 flex items-center justify-center max-h-[260px] my-auto">
                  {/* Pentagon Radar Chart SVG - 5 axes */}
                  <svg className="w-full h-full max-w-[270px]" viewBox="0 0 270 220">
                    <defs>
                      <filter id="radarNeonGlow" x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur stdDeviation="5" result="blur" />
                        <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    {/* Concentric grid rings: solid outer, dashed middle and inner circles matching Figma's large sizes */}
                    <circle cx="135" cy="110" r="95" fill="none" stroke="#222330" strokeWidth="1" />
                    <circle cx="135" cy="110" r="65" fill="none" stroke="#1C1E28" strokeWidth="1" strokeDasharray="3 3" />
                    <circle cx="135" cy="110" r="35" fill="none" stroke="#1C1E28" strokeWidth="1" strokeDasharray="3 3" />
                    {/* Axis crosshair lines (Vertical + Horizontal) extended to fit the 95px radius circles */}
                    <line x1="135" y1="15" x2="135" y2="205" stroke="#222330" strokeWidth="1" />
                    <line x1="40" y1="110" x2="230" y2="110" stroke="#222330" strokeWidth="1" />
                    
                    {/* Data polygon driven by active metrics */}
                    {(() => {
                      const riskScore = isDemoMode ? 95 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.tradeWinRate || '0') * 1.25 || 80));
                      const volScore = isDemoMode ? 82 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.dayWinRate || '0') * 1.1 || 75));
                      const sharpeScore = isDemoMode ? 74 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.tradeWinRate || '0') * 1.05 || 70));
                      const yieldScore = isDemoMode ? 88 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.dayWinRate || '0') * 1.2 || 85));

                      const scores = [riskScore / 100, volScore / 100, sharpeScore / 100, sharpeScore / 100, yieldScore / 100];
                      const maxR = 95;
                      const cx = 135, cy = 110;
                      const angles = [
                        -Math.PI / 2,
                        -Math.PI / 2 + (2 * Math.PI / 5),
                        -Math.PI / 2 + (4 * Math.PI / 5),
                        -Math.PI / 2 + (6 * Math.PI / 5),
                        -Math.PI / 2 + (8 * Math.PI / 5),
                      ];
                      
                      const vertices = scores.map((s, i) => {
                        const r = Math.max(0.05, s) * maxR;
                        const labelNames = ["Risk", "Vol", "Sharpe", "Sharpe", "Yield"];
                        const scoreVals = [riskScore, volScore, sharpeScore, sharpeScore, yieldScore];
                        return {
                          x: cx + r * Math.cos(angles[i]),
                          y: cy + r * Math.sin(angles[i]),
                          label: `${labelNames[i]} (${scoreVals[i]})`,
                          value: i === 2 || i === 3 
                            ? `${sharpeScore} Sharpe Ratio` 
                            : `${scoreVals[i]}% Score`
                        };
                      });

                      const pts = vertices.map(v => `${v.x},${v.y}`).join(' ');

                      return (
                        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }}>
                          <polygon
                            points={pts}
                            fill="rgba(229, 9, 20, 0.15)"
                            stroke="#E50914"
                            strokeWidth="2"
                            filter="url(#radarNeonGlow)"
                          />
                          {/* Vertex interactive hotspots: solid red, 5px radius with thin white stroke matching Figma, color transition only to prevent hover:scale-150 boundary flickering */}
                          {vertices.map((v, i) => (
                            <circle
                              key={i}
                              cx={v.x}
                              cy={v.y}
                              r="5"
                              fill="#E50914"
                              stroke="#FFFFFF"
                              strokeWidth="1.2"
                              className="cursor-pointer transition-colors duration-200 hover:fill-white hover:stroke-[#E50914]"
                              style={{ filter: 'drop-shadow(0 0 3px rgba(229, 9, 20, 0.8))' }}
                              onMouseEnter={() => setHoveredRadar({ x: v.x, y: v.y, label: v.label, value: v.value })}
                              onMouseLeave={() => setHoveredRadar(null)}
                            />
                          ))}
                        </motion.g>
                      );
                    })()}
                    {/* Axis labels perfectly offset from the new large 95px radius circles - consistent between both modes */}
                    <text x="135" y="10" textAnchor="middle" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">
                      Risk ({isDemoMode ? 95 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.tradeWinRate || '0') * 1.25 || 80))})
                    </text>
                    <text x="238" y="113" textAnchor="start" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">
                      Vol ({isDemoMode ? 82 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.dayWinRate || '0') * 1.1 || 75))})
                    </text>
                    <text x="135" y="215" textAnchor="middle" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">
                      Sharpe ({isDemoMode ? 74 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.tradeWinRate || '0') * 1.05 || 70))})
                    </text>
                    <text x="32" y="113" textAnchor="end" fontSize="7.5" fill="#4A4B5E" fontFamily="sans-serif">
                      Yield ({isDemoMode ? 88 : Math.min(99, Math.round(parseFloat(displayPortfolioData?.dayWinRate || '0') * 1.2 || 85))})
                    </text>
                  </svg>
                  
                  {/* Absolute positioning of radar vertex tooltip */}
                  {hoveredRadar && (
                    <div
                      className="absolute bg-neutral-950/95 border border-red-500/30 rounded-xl px-3 py-1.5 text-xs text-white pointer-events-none shadow-2xl backdrop-blur-md z-50 transition-all duration-150"
                      style={{
                        left: `${(hoveredRadar.x / 270) * 100}%`,
                        top: `${(hoveredRadar.y / 220) * 100}%`,
                        transform: 'translate(-50%, -125%)'
                      }}
                    >
                      <p className="font-bold text-neutral-400 text-[9px] uppercase tracking-wider leading-none">{hoveredRadar.label}</p>
                      <p className="text-xs font-extrabold text-[#E50914] mt-1 leading-none">{hoveredRadar.value}</p>
                    </div>
                  )}

                  {/* HTML Absolute center badge so it stays crisp and perfectly layered */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
                    <span className="text-5xl font-black text-white tracking-tighter font-sans mt-3">
                      {isDemoMode ? "92" : Math.min(99, Math.round(parseFloat(displayPortfolioData?.tradeWinRate || '0') * 1.2))}
                    </span>
                    <span className="text-xs font-bold text-[#E50914] tracking-widest mt-1 uppercase select-none">
                      {isDemoMode ? 'A+ TIER' : (parseFloat(displayPortfolioData?.tradeWinRate || '0') > 60 ? 'A+ TIER' : (parseFloat(displayPortfolioData?.tradeWinRate || '0') > 40 ? 'B TIER' : 'C TIER'))}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="bg-[#141414] p-4 rounded-2xl text-center select-none">
                    <p className="text-[9px] text-[#777777] uppercase font-semibold tracking-widest mb-1.5 font-sans">{t('dashboard.riskRating')}</p>
                    <p className="text-sm font-extrabold text-white uppercase font-sans tracking-wide">
                      {isDemoMode ? t('dashboard.excellent') : (parseFloat(displayPortfolioData?.rrRatio?.split(': ')[1] || "0") > 1.5 ? t('dashboard.excellent') : t('dashboard.moderate'))}
                    </p>
                  </div>
                  <div className="bg-[#141414] p-4 rounded-2xl text-center select-none">
                    <p className="text-[9px] text-[#777777] uppercase font-semibold tracking-widest mb-1.5 font-sans">{t('dashboard.consistency')}</p>
                    <p className="text-sm font-extrabold text-white uppercase font-sans tracking-wide">
                      {isDemoMode ? t('dashboard.top5Percent') : (parseFloat(displayPortfolioData?.dayWinRate || "0") > 50 ? t('dashboard.top5Percent') : t('dashboard.developing'))}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Container - Cumulative Equity Curve + Daily Realized Bar Chart */}
            <div className="w-full lg:flex-1 flex flex-col gap-6">
              {/* Equity Line Chart - Card 1 */}
              <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 flex flex-col justify-between w-full min-h-[220px] lg:h-[263px] relative font-sans">
                <div className="flex justify-between items-start mb-2 select-none">
                  <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest">
                    {t('dashboard.cumulativeEquityCurve')}
                  </h3>
                  <div className="text-right">
                    <p className="text-[8px] text-neutral-500 uppercase font-semibold mb-1.5 tracking-wider -translate-y-[4px]">
                      {isDemoMode ? "Y-Axis: Total Balance (USD)" : t('dashboard.yAxisLabel')}
                    </p>
                    <p className="text-[11px] font-bold text-white tracking-tight font-sans animate-fade-in" style={{ fontVariantNumeric: 'tabular-nums' }} data-tour="dashboard-balance">
                      {isDemoMode ? "$148,900" : balance}
                    </p>
                  </div>
                </div>
                
                {/* Line chart container with exact Figma specs: width: 704; height: 176; padding-left: 40px */}
                <div className="w-full h-[176px] flex relative mt-2 min-h-0">
                  {/* Y-axis labels taking up exactly the 40px padding-left space with precise vertical alignment */}
                  <div className="flex flex-col justify-between items-start w-[40px] h-full flex-shrink-0 select-none text-left -translate-y-[4px]">
                    {(() => {
                      if (isDemoMode) {
                        return (
                          <>
                            <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$150,000</span>
                            <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$100,000</span>
                            <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$50,000</span>
                            <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">$0</span>
                          </>
                        );
                      }
                      
                      if (!displayPortfolioData?.performance || displayPortfolioData.performance.length === 0) return null;
                      
                      let b = parseFloat(balance.replace(/[^0-9.-]+/g, ''));
                      if (isNaN(b) || b === 0) return null;
                      const pts: number[] = [b];
                      for (let i = displayPortfolioData.performance.length - 1; i >= 1; i--) {
                        b -= displayPortfolioData.performance[i].realizedValue;
                        pts.unshift(b);
                      }
                      // Need at least 2 points for a meaningful range
                      if (pts.length < 2) {
                        const fmtY = (v: number): string => `$${Math.round(v).toLocaleString('en-US')}`;
                        return <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{fmtY(pts[0])}</span>;
                      }
                      const maxB = Math.max(...pts) * 1.05;
                      const minB = Math.min(...pts) * 0.95;
                      const midB = (maxB + minB) / 2;
                      const fmtY = (v: number): string => `$${Math.round(v).toLocaleString('en-US')}`;
                      return (
                        <>
                          <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{fmtY(maxB)}</span>
                          <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{fmtY(midB)}</span>
                          <span className="text-[7.5px] text-neutral-500 font-sans font-bold tracking-wide leading-none">{fmtY(minB)}</span>
                        </>
                      );
                    })()}
                  </div>
                  
                    {/* Chart area taking up the full remaining 664px width */}
                  <div 
                    className="relative flex-1 min-h-0"
                    onMouseMove={handleEquityMouseMove}
                    onMouseLeave={() => setHoveredEquityPoint(null)}
                  >
                    <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 664 176">
                      <defs>
                        <filter id="equityNeonGlow" x="-20%" y="-20%" width="140%" height="140%">
                          <feGaussianBlur stdDeviation="3" result="blur" />
                          <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                        <linearGradient id="equityGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="rgba(34,197,94,0.45)" />
                          <stop offset="100%" stopColor="rgba(34,197,94,0.0)" />
                        </linearGradient>
                      </defs>
                      {/* Horizontal grid reference lines scaled to 176px height */}
                      {!isDemoMode && (
                        <>
                          <line x1="0" y1="44" x2="664" y2="44" stroke="#1C1D26" strokeWidth="0.5" strokeDasharray="2 2" />
                          <line x1="0" y1="88" x2="664" y2="88" stroke="#1C1D26" strokeWidth="0.5" strokeDasharray="2 2" />
                          <line x1="0" y1="132" x2="664" y2="132" stroke="#1C1D26" strokeWidth="0.5" strokeDasharray="2 2" />
                        </>
                      )}
                      {(() => {
                        if (!displayPortfolioData?.performance || displayPortfolioData.performance.length === 0) return null;
                        
                        const points = isDemoMode 
                          ? [10000, 20000, 45000, 35000, 68000, 80000, 122450, 95000, 130000, 148900, 148900]
                          : (() => {
                              let currentBal = parseFloat(balance.replace(/[^0-9.-]+/g, ''));
                              if (isNaN(currentBal) || currentBal === 0) currentBal = 10000;
                              const pts: number[] = [currentBal];
                              for (let i = 0; i < displayPortfolioData.performance.length - 1; i++) {
                                const item = displayPortfolioData.performance[i];
                                const realizedVal = item && typeof item.realizedValue === 'number' && Number.isFinite(item.realizedValue)
                                  ? item.realizedValue
                                  : 0;
                                currentBal -= realizedVal;
                                pts.unshift(currentBal);
                              }
                              return pts;
                            })();

                        const maxB = isDemoMode ? 150000 : Math.max(...points) * 1.05;
                        const minB = isDemoMode ? 0 : Math.min(...points) * 0.95;

                        // In live mode, require at least 2 data points for a meaningful chart
                        if (!isDemoMode && points.length < 2) {
                          return (
                            <>
                              <line x1="0" y1="105" x2="664" y2="105" stroke="#2A2B3D" strokeWidth="0.8" strokeDasharray="3 3" />
                              <text x="332" y="90" textAnchor="middle" fontSize="11" fill="#4A4B5E" fontFamily="sans-serif">Add more trades to see the equity curve</text>
                            </>
                          );
                        }

                        const range = maxB - minB === 0 ? 1 : maxB - minB;
                        const stepX = 664 / (points.length - 1 || 1);

                        // Build straight-line polyline coordinates mapped in 1:1 pixel units (width: 664px, height: 176px)
                        const coords = points.map((pt, idx) => ({
                          x: idx * stepX,
                          y: 176 - ((pt - minB) / range) * 176
                        }));

                        let pathD = `M${coords[0].x} ${coords[0].y}`;
                        for (let i = 1; i < coords.length; i++) {
                          pathD += ` L${coords[i].x} ${coords[i].y}`;
                        }
                        
                        return (
                          <>
                            {/* Rich, solid dark red gradient fill under the curve matching Figma's exact shadow presence */}
                            <motion.path
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1.0 }}
                              transition={{ delay: 1, duration: 1 }}
                              d={`${pathD} L664 176 L0 176 Z`}
                              fill="url(#equityGradient)"
                            />
                            {/* Crisp, continuous solid 3px red trendline */}
                            <motion.path
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{ duration: 2 }}
                              d={pathD}
                              fill="none"
                              stroke="#22c55e"
                              strokeWidth="3"
                            />
                          </>
                        );
                      })()}
                    </svg>

                    {/* 100% Figma Mockup Peak & End Landmark Circles and Floating HTML Text Labels */}
                    {isDemoMode && (() => {
                      const maxB = 150000;
                      const minB = 0;
                      const range = maxB - minB;
                      
                      const y1 = 100 - ((122450 - minB) / range) * 100;
                      const y2 = 100 - ((148900 - minB) / range) * 100;

                      return (
                        <>
                          {/* $122,450 Peak Marker - Aligned perfectly with index 6 at 60.0% */}
                          <div 
                            className="absolute w-2 h-2 bg-[#E50914] rounded-full z-10 shadow-[0_0_8px_#E50914]" 
                            style={{ left: '60%', top: `${y1}%`, transform: 'translate(-50%, -50%)' }}
                          />
                          <div 
                            className="absolute font-bold text-white text-[11px] font-sans z-10 whitespace-nowrap"
                            style={{ left: '60%', top: `${y1}%`, transform: 'translate(-50%, -180%)' }}
                          >
                            $122,450
                          </div>
                          
                          {/* $148,900 End Marker - Aligned perfectly with the very end of the line at index 10 at 100.0%, with no duplicate text to match Figma explicitly and prevent any vertical collisions */}
                          <div 
                            className="absolute w-2 h-2 bg-[#E50914] rounded-full z-10 shadow-[0_0_8px_#E50914]" 
                            style={{ left: '100%', top: `${y2}%`, transform: 'translate(-50%, -50%)' }}
                          />
                        </>
                      );
                    })()}

                    {/* Interactive dynamic cursor line */}
                    {hoveredEquityPoint && (
                      <div 
                        className="absolute top-0 bottom-0 w-px bg-neutral-700/60 border-dashed pointer-events-none z-10"
                        style={{ left: `${hoveredEquityPoint.x}%` }}
                      />
                    )}

                    {/* Glowing pointer dot */}
                    {hoveredEquityPoint && (
                      <div 
                        className="absolute w-2.5 h-2.5 bg-[#E50914] rounded-full border border-white pointer-events-none z-10 shadow-[0_0_10px_rgba(229,9,20,0.8)]"
                        style={{ 
                          left: `${hoveredEquityPoint.x}%`, 
                          top: `${hoveredEquityPoint.y}%`, 
                          transform: 'translate(-50%, -50%)' 
                        }}
                      />
                    )}

                    {/* Dynamic floating tooltip */}
                    {hoveredEquityPoint && (
                      <div 
                        className="absolute bg-neutral-950/95 border border-[#1C1D26] rounded-xl px-4 py-2.5 text-xs text-white pointer-events-none shadow-2xl backdrop-blur-md z-30 flex flex-col gap-1 min-w-[160px]"
                        style={{ 
                          left: `${hoveredEquityPoint.x}%`, 
                          top: `${hoveredEquityPoint.y}%`,
                          transform: hoveredEquityPoint.x > 70 ? 'translate(-110%, -50%)' : 'translate(15px, -50%)'
                        }}
                      >
                        <div className="flex justify-between items-center gap-4">
                          <span className="text-[9px] text-neutral-500 uppercase font-semibold">Date</span>
                          <span className="font-bold text-neutral-200">{hoveredEquityPoint.date}</span>
                        </div>
                        <div className="flex justify-between items-center gap-4">
                          <span className="text-[9px] text-neutral-500 uppercase font-semibold">Equity</span>
                          <span className="font-extrabold text-white">{hoveredEquityPoint.balance}</span>
                        </div>
                        {hoveredEquityPoint.pnl && (
                          <div className="flex justify-between items-center gap-4 border-t border-neutral-800/60 pt-1 mt-0.5">
                            <span className="text-[9px] text-neutral-500 uppercase font-semibold">Change</span>
                            <span className={`font-bold ${hoveredEquityPoint.positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                              {hoveredEquityPoint.pnl}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Balance label overlay at top-right corner */}
                    {!isDemoMode && (
                      <div className="absolute top-0 right-0 z-10 select-none">
                        <span className="text-xs font-bold text-white bg-[#111111]/90 border border-[#E50914]/30 px-2.5 py-1 rounded-xl backdrop-blur-md whitespace-nowrap shadow-lg">
                          {balance}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bar Chart - Card 2 */}
              <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 flex flex-col justify-between w-full min-h-[180px] lg:h-[215px] gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/[0.01] rounded-full blur-[80px] pointer-events-none" />
                <h3 className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest mb-2 select-none">
                  {isDemoMode 
                    ? `${t('dashboard.netDailyRealized')} (${t('dashboard.september').substring(0, 3).toUpperCase()})` 
                    : `${t('dashboard.netDailyRealized')} (${monthNames[currentDateForDisplay.getMonth()].substring(0, 3)})`
                  }
                </h3>
                {/* Dynamically stretching bottom-aligned bar container matching Figma */}
                <div className="relative flex-1 flex items-stretch justify-around px-4 mt-2 min-h-0 pb-1">
                  {/* Baseline tipis di dasar diagram */}
                  <div className="absolute left-0 right-0 bottom-[22px] h-[1px] bg-[#1C1D26]/70 z-10" />
                  {displayPortfolioData?.performance && displayPortfolioData.performance.length > 0 ? (
                    (isDemoMode 
                      ? displayPortfolioData.performance.filter((d: any) => d.date !== "03" && d.date !== "07").slice(0, 6)
                      : [...displayPortfolioData.performance].slice(0, 6).reverse()
                    ).map((day: any, i: number) => {
                      const maxVal = Math.max(...displayPortfolioData.performance.map((d: any) => Math.abs(d.realizedValue)));
                      const heightPct = maxVal === 0 ? 0 : Math.min(88, Math.max(28, (Math.abs(day.realizedValue) / maxVal) * 88));
                      return (
                        <Bar key={i} heightPct={heightPct} label={day.barLabel || day.realized} date={day.date} positive={day.positive} neutral={day.neutral} />
                      );
                    })
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 py-4">
                      <AlertCircle size={24} className="text-red-500/50" />
                      <p className="text-xs font-bold text-red-500">
                        {dataErrors.performance || 'Belum ada data performa harian'}
                      </p>
                      <p className="text-[10px] text-neutral-600">
                        {t('dashboard.apiConnectedNoHistory')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Performance Calendar */}
          <div className="w-full max-w-[1132px]">
            <ErrorBadge message={dataErrors.performance} />
            <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-6 relative w-full h-auto gap-6 flex flex-col justify-between font-sans overflow-hidden">
              <div className="flex justify-between items-center select-none mb-4">
                <h3 className="text-sm md:text-xl font-bold text-white tracking-tight truncate">
                  {isDemoMode 
                    ? `${t('dashboard.september')} 2024 ${t('dashboard.monthPerformance')}` 
                    : `${monthNames[currentDateForDisplay.getMonth()]} ${currentDateForDisplay.getFullYear()} ${t('dashboard.monthPerformance')}`
                  }
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevMonth}
                    disabled={isDemoMode}
                    className="w-8 h-8 rounded-lg bg-[#111218] border border-[#1C1D26] flex items-center justify-center hover:bg-neutral-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={handleNextMonth}
                    disabled={isDemoMode}
                    className="w-8 h-8 rounded-lg bg-[#111218] border border-[#1C1D26] flex items-center justify-center hover:bg-neutral-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 md:gap-2 bg-[#0A0A0A] rounded-2xl overflow-hidden p-1 md:p-2">
                {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map(day => (
                  <div key={day} className="bg-[#141414] py-3 text-[9px] uppercase font-bold text-[#666666] text-center tracking-widest rounded-lg border border-[#1C1D26]/30">{day}</div>
                ))}

                {Array.from({ length: startOffset }).map((_, idx) => (
                  <CalendarEmpty key={`empty-${idx}`} />
                ))}

                {Array.from({ length: daysInMonth }).map((_, idx) => {
                  const dayNum = idx + 1;
                  const isCurrentMonth = isDemoMode || (currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear());
                  const dayData = isCurrentMonth ? displayPortfolioData?.performance?.find(d => parseInt(d.date) === dayNum) : null;

                  if (dayData) {
                    return (
                      <CalendarDay
                        key={dayNum}
                        day={dayNum < 10 ? `0${dayNum}` : dayNum.toString()}
                        value={dayData.realized}
                        trades={dayData.tradesCount ? `${dayData.tradesCount} ${dayData.tradesCount === 1 ? 'TRADE' : 'TRADES'}` : '-'}
                        positive={dayData.positive}
                        neutral={dayData.neutral}
                      />
                    );
                  }

                  return (
                    <CalendarDay
                      key={dayNum}
                      day={dayNum < 10 ? `0${dayNum}` : dayNum.toString()}
                      value="-"
                      trades="-"
                    />
                  );
                })}
              </div>
            </div>
          </div>

          {/* Active Deployments Table */}
          <div className="w-full max-w-[1132px]">
            <ErrorBadge message={dataErrors.deployments} />
            <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl overflow-hidden mb-12 relative w-full font-sans">
              <div className="flex justify-between items-center select-none w-full h-auto py-4 md:py-6 px-4 md:px-8 border-b border-[#FFFFFF0D] flex-wrap gap-2">
                <div className="space-y-1">
                  <h3 className="text-xl font-bold text-white tracking-tight">{t('dashboard.activeDeployments')}</h3>
                  <p className="text-neutral-500 text-xs font-medium">{t('dashboard.realTimeTelemetry')}</p>
                </div>
                <div className="flex items-center gap-2 bg-[#E50914]/10 px-3 py-1.5 rounded-full border border-[#E50914]/20">
                  <div className="w-1.5 h-1.5 bg-[#E50914] rounded-full animate-pulse" />
                  <span className="text-[10px] font-bold text-[#E50914] uppercase tracking-widest">{isDemoMode ? "LIVE DATA" : t('dashboard.liveData')}</span>
                </div>
              </div>

              <div className="overflow-x-auto w-full -mx-0">
                <table className="w-full text-left border-collapse min-w-[560px]">
                  <thead className="bg-[#14151E]/40 text-[10px] uppercase font-semibold text-[#777777] tracking-widest border-b border-[#1C1D26]/40 select-none">
                    <tr>
                      <th className="px-8 py-4">{t('dashboard.asset')}</th>
                      <th className="px-8 py-4">{t('dashboard.entryMatrix')}</th>
                      <th className="px-8 py-4">{t('dashboard.positionSize')}</th>
                      <th className="px-8 py-4">{t('dashboard.pnlUnrealized')}</th>
                      <th className="px-8 py-4">{t('dashboard.duration')}</th>
                      <th className="px-8 py-4"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800/50">
                    {displayPortfolioData?.activeDeployments && displayPortfolioData.activeDeployments.length > 0 ? (
                      displayPortfolioData.activeDeployments.map((trade: any) => (
                        <TableRow
                          key={trade.id}
                          asset={trade.asset}
                          type={trade.type}
                          entry={trade.entry}
                          size={trade.size}
                          pnl={trade.pnl}
                          percent={trade.percent}
                          duration={trade.duration}
                          positive={trade.positive}
                          exchange={trade.exchange}
                        />
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-8 py-8">
                          <div className="flex flex-col items-center justify-center gap-3">
                            <AlertCircle size={32} className="text-red-500/50" />
                            <div className="text-center">
                              <p className="text-sm font-bold text-red-500 mb-1">
                                {dataErrors.deployments || t('dashboard.noActivePositions')}
                              </p>
                              <p className="text-xs text-neutral-500">
                                {dataErrors.deployments
                                  ? `${t('dashboard.connectedTo')} ${formData.broker?.toUpperCase() || 'exchange'} ${t('dashboard.apiConnectedNoPositions').toLowerCase()}.`
                                  : t('dashboard.connectApiOrManual')
                                }
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="p-4 bg-neutral-900/40 text-center">
                <button className="text-[10px] font-black text-neutral-500 uppercase tracking-widest hover:text-white transition-colors">
                  {t('dashboard.viewDeepAuditLog')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

      {/* Add CEX Account Modal */}
      <AddCexAccountModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAccountAdded}
      />

      {/* Sync Loading Modal */}
      <SyncLoadingModal
        isOpen={isSyncing}
        cexName={selectedAccount?.cex_display_name || 'Exchange'}
        message="Fetching your latest trading data and portfolio balance..."
      />

      {/* Success Toast */}
      <SuccessToast
        isOpen={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        cexName={selectedAccount?.cex_display_name || 'Exchange'}
      />

      {/* Interactive Onboarding Tour */}
      <ProductTour steps={tourSteps} tourKey="dashboard" />

      {/* Recurring Pricing Popup for Trial Users */}
      <AnimatePresence>
        {showPricingPopup && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md font-sans">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`bg-[#0A0A0F] border border-neutral-800 rounded-3xl p-6 md:p-8 ${triggerCount === 0 ? 'max-w-4xl' : 'max-w-2xl'} w-full relative shadow-2xl overflow-hidden font-sans`}
            >
              <div className="absolute -top-32 -right-32 w-64 h-64 bg-[#E50914]/10 rounded-full blur-[90px] pointer-events-none" />
              
              <button 
                onClick={() => {
                  setShowPricingPopup(false);
                  setTriggerCount(prev => prev + 1);
                }}
                className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>

              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-950/30 border border-red-900/40 rounded-full mb-3 select-none">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#E50914] animate-pulse" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-[#E50914]">TRIAL ACTIVE</span>
                </div>
                <h3 className="text-xl md:text-2xl font-black text-white">Upgrade to Premium</h3>
                <p className="text-xs text-neutral-400 mt-1">
                  You are currently using your free Pro trial. Upgrade now to secure permanent access to advanced features.
                </p>

                {/* Cycle Toggle */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button 
                    onClick={() => setCycle('monthly')}
                    className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${cycle === 'monthly' ? 'bg-[#E50914] text-white font-sans' : 'text-neutral-500 hover:text-white font-sans'}`}
                  >
                    Monthly
                  </button>
                  <button 
                    onClick={() => setCycle('yearly')}
                    className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${cycle === 'yearly' ? 'bg-[#E50914] text-white font-sans' : 'text-neutral-500 hover:text-white font-sans'}`}
                  >
                    Yearly
                    <span className="text-[8px] bg-white text-black font-extrabold px-1.5 py-0.5 rounded-full uppercase tracking-wider font-sans">Save 20%+</span>
                  </button>
                </div>
              </div>

              <div className={`grid grid-cols-1 ${triggerCount === 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                {/* Free Plan (only visible on first load, triggerCount === 0) */}
                {triggerCount === 0 && (
                  <div className="bg-neutral-950/60 border border-neutral-900 rounded-2xl p-5 relative flex flex-col justify-between text-left">
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="text-base font-extrabold text-white">Free Plan</h4>
                          <p className="text-[10px] text-neutral-500">Basic Tracking</p>
                        </div>
                        <div className="text-right">
                          <p className="text-base font-black text-white font-mono">IDR 0</p>
                          <p className="text-[8px] text-neutral-500 uppercase">/ month</p>
                        </div>
                      </div>
                      <ul className="space-y-2.5 my-4">
                        {['Manual Trades (max 5/mo)', 'Basic Trade Journal', 'TradingView Chart Integration', '1 Week Free Pro Trial'].map(f => (
                          <li key={f} className="flex items-center gap-2 text-[10px] text-neutral-300">
                            <CheckCircle2 size={11} className="text-neutral-500 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button 
                      onClick={() => {
                        setShowPricingPopup(false);
                        setTriggerCount(prev => prev + 1);
                      }}
                      className="w-full py-2.5 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 text-white font-bold rounded-xl text-xs tracking-wide transition-all cursor-pointer text-center"
                    >
                      Continue with Free
                    </button>
                  </div>
                )}
                {/* Pro Plan */}
                <div className="bg-neutral-950/60 border border-neutral-900 hover:border-neutral-800 rounded-2xl p-5 relative flex flex-col justify-between text-left">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="text-base font-extrabold text-white">Pro Package</h4>
                        <p className="text-[10px] text-neutral-500">For Active Traders</p>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-white font-mono">
                          {cycle === 'monthly' ? 'IDR 99k' : 'IDR 949k'}
                        </p>
                        <p className="text-[8px] text-neutral-500 uppercase">/ {cycle === 'monthly' ? 'month' : 'year'}</p>
                      </div>
                    </div>
                    
                    <ul className="space-y-2.5 my-4">
                      {['Auto-Sync API Import', 'Calendar Daily Journal', 'Unlimited Manual Trades', 'Multi-Variable Analytics', 'Portfolio Performance Tracker'].map(f => (
                        <li key={f} className="flex items-center gap-2 text-[10px] text-neutral-300">
                          <CheckCircle2 size={11} className="text-[#E50914] shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button 
                    onClick={() => handleSubscribe('pro')}
                    disabled={loadingPlan !== null}
                    className="w-full py-2.5 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 text-white font-bold rounded-xl text-xs tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {loadingPlan === 'pro' ? (
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : 'Upgrade to Pro'}
                  </button>
                </div>

                {/* Premium Plan */}
                <div className="bg-gradient-to-br from-neutral-950 to-neutral-950/80 border border-[#E50914]/40 hover:border-[#E50914] rounded-2xl p-5 relative flex flex-col justify-between text-left">
                  <span className="absolute -top-2.5 right-4 bg-[#E50914] text-white text-[8px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-widest">
                    Best Value
                  </span>
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="text-base font-extrabold text-white flex items-center gap-1">
                          <Crown size={14} className="text-[#E50914]" />
                          Premium
                        </h4>
                        <p className="text-[10px] text-neutral-500">For Advance Traders</p>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-black text-white font-mono">
                          {cycle === 'monthly' ? 'IDR 179k' : 'IDR 1.499m'}
                        </p>
                        <p className="text-[8px] text-neutral-500 uppercase">/ {cycle === 'monthly' ? 'month' : 'year'}</p>
                      </div>
                    </div>
                    
                    <ul className="space-y-2.5 my-4">
                      {['All Pro Features', 'CatatCrypto Score Dashboard', 'Personalized AI Assistant (Beta)', 'Dev Picks & News Integrations', 'Early Access to New Features'].map(f => (
                        <li key={f} className="flex items-center gap-2 text-[10px] text-neutral-300">
                          <CheckCircle2 size={11} className="text-[#E50914] shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button 
                    onClick={() => handleSubscribe('premium')}
                    disabled={loadingPlan !== null}
                    className="w-full py-2.5 bg-[#E50914] hover:bg-[#E50914]/90 text-white font-bold rounded-xl text-xs tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {loadingPlan === 'premium' ? (
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : 'Go Premium'}
                  </button>
                </div>
              </div>

              {popupError && (
                <div className="mt-4 p-3 bg-red-900/20 border border-red-900/45 rounded-xl text-[#EF4444] text-[11px] text-center">
                  {popupError}
                </div>
              )}

              {waitingPaymentPlan && (
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-900/40 rounded-xl text-yellow-500 text-[11px] text-center animate-pulse">
                  Waiting for payment... Please complete the checkout in the new tab.
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Sub Components ---

const ErrorBadge = ({ message }: { message?: string }) => {
  if (!message) return null;
  return (
    <div className="mb-2">
      <div className="bg-red-900/80 border border-red-800/50 rounded-lg px-2.5 py-1 flex items-center gap-1.5 shadow-sm">
        <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
        <span className="text-[9px] font-bold text-red-300 uppercase tracking-wider leading-tight">{message}</span>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, subValue, positive = false, custom = false, error }: any) => {
  const isAllWinLoss = custom && value && typeof value === 'string' && value.includes(' / ');
  
  const [displayValue, setDisplayValue] = useState<string>('');
  const [displaySubValue, setDisplaySubValue] = useState<string>('');

  useEffect(() => {
    if (isAllWinLoss) {
      const [win, loss] = value.split(' / ');
      let currentWin = 0;
      let currentLoss = 0;
      const targetWin = parseFloat(win.replace(/[^0-9.-]/g, ''));
      const targetLoss = parseFloat(loss.replace(/[^0-9.-]/g, ''));
      
      const duration = 1400;
      const steps = 50;
      const stepDuration = duration / steps;
      
      let step = 0;
      const interval = setInterval(() => {
        step++;
        const progress = step / steps;
        const eased = 1 - Math.pow(1 - progress, 3);
        
        currentWin = targetWin * eased;
        currentLoss = targetLoss * eased;
        
        setDisplayValue(`$${Math.round(currentWin)} / $${Math.round(currentLoss)}`);
        
        if (step >= steps) {
          setDisplayValue(value);
          clearInterval(interval);
        }
      }, stepDuration);
      
      return () => clearInterval(interval);
    } else if (typeof value === 'string' && (value.includes('$') || value.includes('%'))) {
      const numStr = value.replace(/[^0-9.-]/g, '');
      const targetNum = parseFloat(numStr);
      
      if (!isNaN(targetNum)) {
        let current = 0;
        const duration = 1400;
        const steps = 50;
        const stepDuration = duration / steps;
        
        let step = 0;
        const interval = setInterval(() => {
          step++;
          const progress = step / steps;
          const eased = 1 - Math.pow(1 - progress, 3);
          
          current = targetNum * eased;
          
          if (value.includes('%')) {
            setDisplayValue(`${current.toFixed(1)}%`);
          } else if (value.includes('$')) {
            const formatted = current.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            });
            setDisplayValue(formatted);
          }
          
          if (step >= steps) {
            setDisplayValue(value);
            clearInterval(interval);
          }
        }, stepDuration);
        
        return () => clearInterval(interval);
      } else {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue(value);
    }
  }, [value, isAllWinLoss]);

  useEffect(() => {
    if (subValue && typeof subValue === 'string') {
      const match = subValue.match(/(\d+)\s*\/\s*(\d+)/);
      if (match) {
        const targetFirst = parseInt(match[1]);
        const targetSecond = parseInt(match[2]);
        const textAfter = subValue.replace(/\d+\s*\/\s*\d+/, '').trim();
        
        let current1 = 0;
        let current2 = 0;
        const duration = 1400;
        const steps = 50;
        const stepDuration = duration / steps;
        
        let step = 0;
        const interval = setInterval(() => {
          step++;
          const progress = step / steps;
          const eased = 1 - Math.pow(1 - progress, 3);
          
          current1 = Math.round(targetFirst * eased);
          current2 = Math.round(targetSecond * eased);
          
          setDisplaySubValue(`${current1} / ${current2} ${textAfter}`);
          
          if (step >= steps) {
            setDisplaySubValue(subValue);
            clearInterval(interval);
          }
        }, stepDuration);
        
        return () => clearInterval(interval);
      } else {
        setDisplaySubValue(subValue);
      }
    } else {
      setDisplaySubValue(subValue);
    }
  }, [subValue]);
  
  return (
    <div className="font-sans h-full flex flex-col">
      <ErrorBadge message={error} />
      <div className="bg-[#1B1B1B] shadow-[0px_1px_1px_1px_#FFFFFF0D_inset] rounded-2xl p-3 md:p-6 hover:shadow-[0px_1px_1px_1px_#FFFFFF0D_inset,0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-300 group relative overflow-hidden select-none flex-1 flex flex-col justify-between min-h-[90px] md:min-h-[102px]">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/[0.01] to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <LayoutGrid size={12} className="text-neutral-700 hover:text-white transition-colors" />
        </div>
        <p className="text-[10px] uppercase font-semibold text-neutral-500 tracking-widest mb-1 select-none">{label}</p>
        
        {isAllWinLoss ? (
          <div className="flex justify-between items-center mt-3 gap-2">
            <h2 className="text-sm md:text-xl font-black text-white tracking-tight font-sans flex items-center gap-1.5 flex-wrap">
              <span className="text-[#10B981]" style={getDynamicFontSizeStyle(displayValue.split(' / ')[0], 14, 20)}>{displayValue.split(' / ')[0]}</span>
              <span className="text-neutral-600 font-normal text-sm select-none">/</span>
              <span className="text-[#EF4444]" style={getDynamicFontSizeStyle(displayValue.split(' / ')[1], 14, 20)}>{displayValue.split(' / ')[1]}</span>
            </h2>
            {displaySubValue && (
              <span className="text-[9px] font-bold text-neutral-400 bg-neutral-800/80 border border-neutral-700/50 px-2 py-0.5 rounded-lg font-sans tracking-wide shrink-0">
                {displaySubValue}
              </span>
            )}
          </div>
        ) : positive && !custom ? (
          <div className="flex items-baseline gap-2.5 mt-2.5">
            <h2 className="text-xl md:text-3xl font-bold text-white tracking-tight font-sans" style={getDynamicFontSizeStyle(displayValue, 20, 30)}>{displayValue}</h2>
            {displaySubValue && (
              <span className="text-[9px] md:text-[10px] font-bold text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-1.5 md:px-2 py-0.5 rounded-xl">
                ▲ {displaySubValue}
              </span>
            )}
          </div>
        ) : (
          <div className="flex justify-between items-baseline mt-2.5">
            <h2 className="text-xl md:text-3xl font-bold text-white tracking-tight font-sans" style={getDynamicFontSizeStyle(displayValue, 20, 30)}>{displayValue}</h2>
            {displaySubValue && (
              <span className="text-[10px] font-semibold text-neutral-500 font-sans tracking-wider uppercase">
                {displaySubValue}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Bar = ({ heightPct, label, date, positive = false, neutral = false }: any) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="flex-1 flex flex-col items-center justify-end h-full select-none relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!neutral && (
        <motion.span 
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="text-[9px] font-semibold text-white mb-2 whitespace-nowrap font-sans leading-none z-20"
        >
          {label}
        </motion.span>
      )}

      {neutral ? (
        <motion.div 
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full max-w-[76px] h-[1px] bg-neutral-700/40 z-20 mb-[22px]"
        />
      ) : (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: `${heightPct}%`, opacity: 1 }}
          whileHover={{ 
            scaleX: 1.05,
            filter: positive ? 'drop-shadow(0 0 12px rgba(16,185,129,0.6))' : 'drop-shadow(0 0 12px rgba(239,68,68,0.6))'
          }}
          whileTap={{}}
          transition={{ duration: 1.1, ease: [0.34, 1.56, 0.64, 1] }}
          style={{ transformOrigin: 'bottom', filter: 'none' }}
          className={`w-full max-w-[76px] rounded-t-xl cursor-pointer z-20 mb-[22px] relative overflow-hidden ${
            positive 
              ? 'bg-gradient-to-t from-[#10B981]/20 via-[#10B981]/60 to-[#10B981]' 
              : 'bg-gradient-to-t from-[#EF4444]/20 via-[#EF4444]/60 to-[#EF4444]'
          }`}
        >
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-transparent to-white/20"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      )}

      {isHovered && !neutral && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          className="absolute z-30 bg-neutral-950/95 border border-[#1C1D26] rounded-xl px-3 py-2 text-xs text-white pointer-events-none shadow-2xl backdrop-blur-md flex flex-col gap-1 whitespace-nowrap min-w-[100px] bottom-[105%] mb-2"
        >
          <div className="flex justify-between items-center gap-2">
            <span className="text-[9px] text-neutral-500 uppercase font-semibold">Day:</span>
            <span className="font-bold text-neutral-300">Date {date}</span>
          </div>
          <div className="flex justify-between items-center gap-2">
            <span className="text-[9px] text-neutral-500 uppercase font-semibold">Realized:</span>
            <span className={`font-bold ${positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
              {label}
            </span>
          </div>
        </motion.div>
      )}

      <motion.span 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="text-[8px] font-semibold text-[#777777] font-sans uppercase tracking-wider leading-none h-[12px] flex items-center justify-center"
      >
        {date}
      </motion.span>
    </div>
  );
};

const CalendarDay = ({ day, value, trades, positive = false, neutral = false }: any) => {
  const hasTrades = value !== '-' && value !== 'NO TRADES';
  const isNoTrades = value === 'NO TRADES' || neutral;

  const bgClass = hasTrades 
    ? 'bg-[#0F0F0F] hover:bg-[#141414]' 
    : 'bg-[#0A0A0A] hover:bg-[#0D0D0D]';

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: parseInt(day) * 0.02 }}
      whileHover={{ scale: 1.02, boxShadow: hasTrades ? (positive ? '0 0 20px rgba(16,185,129,0.15)' : '0 0 20px rgba(239,68,68,0.15)') : 'none' }}
      className={`p-1 md:p-4 h-[44px] md:h-[90px] flex flex-col justify-between group transition-all cursor-pointer relative overflow-hidden select-none ${bgClass} border border-[#1C1D26]/30 rounded-lg`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.01] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      
      <div className="flex justify-between items-start w-full relative z-10">
        <motion.span 
          initial={{ opacity: 0, x: -5 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: parseInt(day) * 0.02 + 0.1 }}
          className={`text-[8px] md:text-[11px] font-bold font-sans ${hasTrades ? 'text-neutral-300' : 'text-[#555555]'}`}
        >
          {day}
        </motion.span>

        {hasTrades && trades !== '-' && (
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: parseInt(day) * 0.02 + 0.2 }}
            className={`hidden md:block text-[7px] font-bold px-1.5 py-1 rounded uppercase tracking-wider font-sans ${
              !positive 
                ? 'bg-[#EF4444]/15 border border-[#EF4444]/30 text-[#EF4444]' 
                : 'bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981]'
            }`}
          >
            {trades}
          </motion.span>
        )}

        {isNoTrades && (
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: parseInt(day) * 0.02 + 0.2 }}
            className="text-[7px] font-bold text-neutral-600 uppercase tracking-wider font-sans"
          >
            NO TRADES
          </motion.span>
        )}
      </div>
      
      <div className="mt-auto w-full flex flex-col justify-end relative z-10">
        {hasTrades ? (
          <>
            <motion.p 
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: parseInt(day) * 0.02 + 0.3 }}
              className={`text-[8px] md:text-[12px] font-black tracking-tight font-sans leading-none mb-1 md:mb-2 ${
                positive ? 'text-[#10B981]' : 'text-[#EF4444]'
              }`}
            >
              {value}
            </motion.p>
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: parseInt(day) * 0.02 + 0.4, duration: 0.5 }}
              className="hidden md:block w-full h-[3px] bg-[#1C1D26] rounded-full overflow-hidden relative"
              style={{ transformOrigin: 'left' }}
            >
              <motion.div 
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: parseInt(day) * 0.02 + 0.5, duration: 0.6 }}
                className={`h-full rounded-full ${positive ? 'bg-[#10B981]' : 'bg-[#EF4444]'}`}
                style={{ 
                  width: positive ? '70%' : '35%',
                  transformOrigin: 'left'
                }}
              />
            </motion.div>
          </>
        ) : (
          <p className="text-[10px] font-semibold tracking-tight font-sans text-neutral-800 leading-none">-</p>
        )}
      </div>
    </motion.div>
  );
};

const CalendarEmpty = () => (
  <div className="bg-[#0A0A0A] p-1 md:p-4 h-[44px] md:h-[90px] opacity-20 border border-[#1C1D26]/20 rounded-lg"></div>
);

const TableRow = ({ asset, type, entry, size, pnl, percent, duration, positive = false, exchange }: any) => {
  const [imgError, setImgError] = useState(false);
  const displayType = type || "";
  const isLong = displayType.toUpperCase().includes("LONG") || displayType.toUpperCase().includes("BUY");
  const isShort = displayType.toUpperCase().includes("SHORT") || displayType.toUpperCase().includes("SELL");

  const leverageMatch = displayType.match(/(\d+)/);
  const leverage = leverageMatch ? leverageMatch[1] : null;

  let badgeText = "";
  if (isLong) {
    badgeText = leverage ? `LONG (${leverage}X)` : "LONG";
  } else if (isShort) {
    badgeText = leverage ? `SHORT (${leverage}X)` : "SHORT";
  } else {
    badgeText = displayType;
  }

  const isDerivative = isLong || isShort;

  let displayPercent = percent || "";
  if (positive && !displayPercent.startsWith('+') && displayPercent !== '-') {
    displayPercent = '+' + displayPercent;
  }

  const getSymbol = (assetString: string) => {
    if (!assetString) return "btc";
    const base = assetString.split('/')[0].trim().toLowerCase();
    return base;
  };
  const symbol = getSymbol(asset);
  const logoUrl = `https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/${symbol}.png`;

  return (
    <tr className="hover:bg-neutral-800/10 transition-colors group cursor-pointer border-b border-[#1C1D26]/40 last:border-none">
      <td className="px-8 py-5">
        <div className="flex items-center gap-3">
          {!imgError ? (
            <img 
              src={logoUrl} 
              alt={asset} 
              className="w-8 h-8 rounded-full object-contain shrink-0"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs select-none shrink-0 ${
              asset.includes('BTC') 
                ? 'bg-gradient-to-tr from-[#F59E0B] to-[#D97706] text-black shadow-[0_0_12px_rgba(245,158,11,0.2)]' 
                : 'bg-gradient-to-tr from-[#8B5CF6] to-[#6D28D9] text-white shadow-[0_0_12px_rgba(139,92,246,0.2)]'
            }`}>
              {asset.includes('BTC') ? 'B' : asset.includes('ETH') ? 'Ξ' : asset[0]}
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold text-white mb-0.5 tracking-tight font-sans">{asset}</p>
              {exchange && (
                <span className="text-[8px] font-bold bg-[#14151E] text-neutral-400 px-1.5 py-0.5 rounded border border-[#27272A]/40 uppercase tracking-widest font-sans select-none">
                  {exchange}
                </span>
              )}
            </div>
            {isDerivative ? (
              <span className={`text-[9px] font-extrabold tracking-wider font-sans ${isLong ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {badgeText}
              </span>
            ) : (
              <span className="text-[9px] font-extrabold text-neutral-500 uppercase tracking-wider font-sans">
                {badgeText}
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-8 py-5 text-xs font-semibold text-neutral-300 font-sans tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>{entry}</td>
      <td className="px-8 py-5 text-xs font-semibold text-neutral-300 font-sans tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>{size}</td>
      <td className="px-8 py-5">
        <p className={`text-xs font-bold ${positive ? 'text-[#10B981]' : 'text-[#EF4444]'} mb-0.5 font-sans tracking-wide`} style={{ fontVariantNumeric: 'tabular-nums' }}>{pnl}</p>
        <p className={`text-[9px] font-semibold ${positive ? 'text-[#10B981]/80' : 'text-[#EF4444]/80'} tracking-widest font-sans`} style={{ fontVariantNumeric: 'tabular-nums' }}>{displayPercent}</p>
      </td>
      <td className="px-8 py-5 text-xs font-semibold text-neutral-400 font-sans tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>{duration}</td>
      <td className="px-8 py-5 text-right">
        <button className="text-neutral-600 hover:text-white transition-colors"><MoreHorizontal size={18} /></button>
      </td>
    </tr>
  );
};

export default DashboardPage;
